import React, { useState } from 'react';
import api from '../utils/api';
import { FileText, Download, RefreshCw, BarChart2, TrendingUp } from 'lucide-react';

const REPORT_CATEGORIES = [
  {
    category: 'Maintenance Reports',
    reports: [
      { key: 'overdue-jobs', label: 'Overdue Jobs Report' },
      { key: 'maintenance-history', label: 'Maintenance History Report' },
      { key: 'job-completion-summary', label: 'Job Completion Summary' },
      { key: 'deferred-jobs', label: 'Deferred Jobs Report' },
      { key: 'ai-predicted-report', label: 'AI Predicted Jobs Report' },
      { key: 'equipment-defect-history', label: 'Equipment Defect History' },
      { key: 'work-plan-status', label: 'Work Plan Status Report' },
    ]
  },
  {
    category: 'Inventory Reports',
    reports: [
      { key: 'current-stock', label: 'Current Stock Report' },
      { key: 'low-stock', label: 'Low Stock Report' },
      { key: 'critical-spares-status', label: 'Critical Spares Status' },
      { key: 'parts-consumption', label: 'Parts Consumption Report' },
      { key: 'slow-moving-stock', label: 'Slow-Moving Stock Report' },
    ]
  },
  {
    category: 'Purchasing Reports',
    reports: [
      { key: 'open-requisitions', label: 'Open Requisitions Report' },
      { key: 'po-status', label: 'Purchase Order Status' },
      { key: 'procurement-spend', label: 'Procurement Spend Report' },
      { key: 'supplier-performance', label: 'Supplier Performance Report' },
      { key: 'emergency-purchases', label: 'Emergency Purchase Report' },
    ]
  },
  {
    category: 'Compliance & Class Reports',
    reports: [
      { key: 'pms-compliance', label: 'PMS Compliance Report' },
      { key: 'statutory-jobs', label: 'Statutory Jobs Status' },
      { key: 'certificate-expiry', label: 'Certificate Expiry Report' },
      { key: 'audit-trail', label: 'Audit Trail Report' },
    ]
  },
];

export default function Reports() {
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});

  const runReport = async (key) => {
    setSelectedReport(key);
    setLoading(true);
    setReportData(null);
    try {
      const res = await api.get(`/reports/${key}`, { params: filters });
      setReportData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    if (!reportData) return;
    const isArray = Array.isArray(reportData);
    const rows = isArray ? reportData : [reportData];
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedReport}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <p className="text-secondary">Select a report to generate. Data can be exported as CSV.</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="form-input" style={{ width: 180 }} placeholder="From date" type="date" onChange={e => setFilters({ ...filters, from_date: e.target.value })} />
          <input className="form-input" style={{ width: 180 }} placeholder="To date" type="date" onChange={e => setFilters({ ...filters, to_date: e.target.value })} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20 }}>
        {/* Report list */}
        <div>
          {REPORT_CATEGORIES.map(cat => (
            <div key={cat.category} className="card mb-16">
              <div className="card-header"><h3 style={{ fontSize: 12 }}>{cat.category}</h3></div>
              <div className="card-body" style={{ padding: 0 }}>
                {cat.reports.map(r => (
                  <div
                    key={r.key}
                    className={`clickable ${selectedReport === r.key ? '' : ''}`}
                    onClick={() => runReport(r.key)}
                    style={{
                      padding: '10px 16px',
                      fontSize: 13,
                      borderBottom: '1px solid var(--border)',
                      background: selectedReport === r.key ? '#f0fdf4' : 'transparent',
                      color: selectedReport === r.key ? 'var(--success)' : 'var(--text)',
                      fontWeight: selectedReport === r.key ? 600 : 400,
                    }}
                  >
                    <FileText size={14} style={{ marginRight: 8, opacity: 0.6 }} />
                    {r.label}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Report output */}
        <div>
          {!selectedReport && (
            <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
              <div className="empty-state">
                <BarChart2 />
                <h4>Select a report</h4>
                <p>Choose a report from the list to generate it.</p>
              </div>
            </div>
          )}

          {selectedReport && loading && (
            <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
              <div className="loading-row">Generating report...</div>
            </div>
          )}

          {selectedReport && reportData && !loading && (
            <div className="card">
              <div className="card-header">
                <h3>{REPORT_CATEGORIES.flatMap(c => c.reports).find(r => r.key === selectedReport)?.label}</h3>
                <button className="btn btn-outline btn-sm" onClick={exportCSV}><Download size={14} /> Export CSV</button>
              </div>
              <ReportRenderer reportKey={selectedReport} data={reportData} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportRenderer({ reportKey, data }) {
  if (!data) return null;

  // Summary report (compliance, etc.)
  if (reportKey === 'pms-compliance') {
    return (
      <div className="card-body">
        <div className="stat-cards" style={{ marginBottom: 20 }}>
          <div className="stat-card"><div className="stat-content"><div className="stat-value">{data.total_scheduled || 0}</div><div className="stat-label">Scheduled</div></div></div>
          <div className="stat-card"><div className="stat-content"><div className="stat-value text-success">{data.completed || 0}</div><div className="stat-label">Completed</div></div></div>
          <div className="stat-card"><div className="stat-content"><div className="stat-value text-danger">{data.overdue || 0}</div><div className="stat-label">Overdue</div></div></div>
          <div className="stat-card"><div className="stat-content"><div className="stat-value text-warning">{data.complianceRate || 0}%</div><div className="stat-label">Compliance Rate</div></div></div>
        </div>
        {data.byDept?.length > 0 && (
          <>
            <h4 className="mb-8">By Department</h4>
            <table>
              <thead><tr><th>Department</th><th>Total</th><th>Completed</th><th>Overdue</th></tr></thead>
              <tbody>{data.byDept.map(d => <tr key={d.department}><td>{d.department}</td><td>{d.total}</td><td>{d.completed}</td><td style={{ color: d.overdue > 0 ? 'var(--danger)' : 'var(--text)' }}>{d.overdue}</td></tr>)}</tbody>
            </table>
          </>
        )}
      </div>
    );
  }

  if (reportKey === 'maintenance-history' || reportKey === 'overdue-jobs' || reportKey === 'deferred-jobs' || reportKey === 'statutory-jobs') {
    const rows = Array.isArray(data) ? data : [];
    if (rows.length === 0) return <div className="card-body"><p className="text-secondary">No data found.</p></div>;
    const headers = Object.keys(rows[0]);
    return (
      <div className="table-container">
        <table>
          <thead><tr>{headers.map(h => <th key={h}>{h.replace(/_/g, ' ')}</th>)}</tr></thead>
          <tbody>
            {rows.map((row, i) => <tr key={i}>{headers.map(h => <td key={h}>{String(row[h] ?? '—')}</td>)}</tr>)}
          </tbody>
        </table>
      </div>
    );
  }

  if (reportKey === 'job-completion-summary') {
    const { summary, byDept, byPriority, trend } = data;
    return (
      <div className="card-body">
        <div className="stat-cards" style={{ marginBottom: 20 }}>
          <div className="stat-card"><div className="stat-content"><div className="stat-value">{summary.total_jobs}</div><div className="stat-label">Total Jobs</div></div></div>
          <div className="stat-card"><div className="stat-content"><div className="stat-value text-success">{summary.closed_jobs}</div><div className="stat-label">Closed</div></div></div>
          <div className="stat-card"><div className="stat-content"><div className="stat-value">{summary.total_hours?.toFixed(1) || 0}h</div><div className="stat-label">Total Hours</div></div></div>
          <div className="stat-card"><div className="stat-content"><div className="stat-value">{summary.avg_hours?.toFixed(1) || 0}h</div><div className="stat-label">Avg Hours/Job</div></div></div>
        </div>
        {byDept?.length > 0 && <><h4 className="mb-8">By Department</h4><table><thead><tr><th>Department</th><th>Total</th><th>Closed</th><th>Avg Hours</th></tr></thead><tbody>{byDept.map(d => <tr key={d.department}><td>{d.department}</td><td>{d.total}</td><td>{d.closed}</td><td>{d.avg_hours?.toFixed(1)}</td></tr>)}</tbody></table></>}
        {trend?.length > 0 && <><h4 className="mb-8 mt-16">Monthly Trend</h4><table><thead><tr><th>Month</th><th>Completed Jobs</th></tr></thead><tbody>{trend.map(t => <tr key={t.month}><td>{t.month}</td><td>{t.count}</td></tr>)}</tbody></table></>}
      </div>
    );
  }

  if (reportKey === 'certificate-expiry') {
    const rows = Array.isArray(data) ? data : [];
    return (
      <div className="table-container">
        <table>
          <thead><tr><th>Certificate</th><th>Issuing Authority</th><th>Issue Date</th><th>Expiry Date</th><th>Days Until Expiry</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map(r => <tr key={r.id}><td>{r.certificate_name}</td><td>{r.issuing_authority}</td><td>{r.issue_date}</td><td>{r.expiry_date}</td><td style={{ color: r.days_until_expiry < 30 ? 'var(--danger)' : r.days_until_expiry < 90 ? 'var(--warning)' : 'var(--text)' }}>{Math.round(r.days_until_expiry)} days</td><td><span className={`badge badge-${r.status.toLowerCase().replace(' ', '')}`}>{r.status}</span></td></tr>)}
          </tbody>
        </table>
      </div>
    );
  }

  if (reportKey === 'audit-trail') {
    const rows = Array.isArray(data) ? data : [];
    return (
      <div className="table-container">
        <table>
          <thead><tr><th>Time</th><th>User</th><th>Action</th><th>Table</th><th>Record</th></tr></thead>
          <tbody>
            {rows.map(r => <tr key={r.id}><td style={{ fontSize: 11 }}>{r.timestamp}</td><td>{r.user_name || r.user_id}</td><td><span className="badge badge-draft">{r.action}</span></td><td>{r.table_name}</td><td>{r.record_id?.slice(0, 8)}...</td></tr>)}
          </tbody>
        </table>
      </div>
    );
  }

  // Generic table
  const rows = Array.isArray(data) ? data : (data.vessels ? data.vessels : []);
  if (rows.length === 0) return <div className="card-body"><p className="text-secondary">No data found.</p></div>;
  const headers = Object.keys(rows[0]);

  return (
    <div className="table-container">
      <table>
        <thead><tr>{headers.map(h => <th key={h}>{h.replace(/_/g, ' ').toUpperCase()}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {headers.map(h => {
                const val = row[h];
                if (typeof val === 'boolean') return <td key={h}>{val ? 'Yes' : 'No'}</td>;
                return <td key={h}>{val != null ? String(val) : '—'}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
